import type { CartItem } from './orderStore';
import { resolveVariantMoq } from '../utils/minOrderQuantity';

const CART_STORAGE_KEY = 'lce_buyer_cart_v1';
const CART_IMPORT_TOKEN_KEY = 'lce_buyer_cart_v1_import_token';

export type PersistedCartLine = {
  productId: number;
  quantity: number;
  effectivePrice: number;
  productVariantId?: number | null;
  variantLabel?: string | null;
  specRemark?: string | null;
  vatInvoiceType?: 'SPECIAL' | 'REGULAR' | null;
  /** 加入/最近更新时间戳 */
  addedAt?: number;
  /** 精简商品快照，避免持久化后列表字段缺失 */
  productSnapshot: {
    id: number;
    sku: string;
    name: string;
    unit: string;
    category?: string | null;
    min_order_quantity?: number;
    supplier_id?: number;
    image_urls?: string[];
    has_variants?: boolean;
    tax_rate?: number | string | null;
    tax_rate_special?: number | string | null;
    tax_rate_regular?: number | string | null;
    freight_fee?: number | string | null;
    free_shipping_min_qty?: number | null;
    freight_tiers?: unknown;
    /** 多规格行：持久化规格起订量，刷新后仍能按倍数改数量 */
    variant_min_order_quantity?: number | null;
  };
};

export function cartToPersistedLines(cart: CartItem[]): PersistedCartLine[] {
  return cart.map((item) => {
    const vid = item.productVariantId ?? null;
    const variant =
      vid != null ? item.product.variants?.find((v) => v.id === vid) ?? null : null;
    const variantMoq =
      variant != null ? resolveVariantMoq(item.product, variant) : null;
    return {
      productId: item.product.id,
      quantity: item.quantity,
      effectivePrice: item.effectivePrice,
      productVariantId: item.productVariantId ?? null,
      variantLabel: item.variantLabel ?? null,
      specRemark: item.specRemark ?? null,
      vatInvoiceType: item.vatInvoiceType ?? null,
      addedAt: item.addedAt,
      productSnapshot: {
        id: item.product.id,
        sku: item.product.sku,
        name: item.product.name,
        unit: item.product.unit,
        category: item.product.category,
        min_order_quantity: item.product.min_order_quantity,
        supplier_id: item.product.supplier_id,
        image_urls: item.product.image_urls,
        has_variants: item.product.has_variants,
        tax_rate: item.product.tax_rate,
        tax_rate_special: item.product.tax_rate_special,
        tax_rate_regular: item.product.tax_rate_regular,
        freight_fee: item.product.freight_fee,
        free_shipping_min_qty: item.product.free_shipping_min_qty,
        freight_tiers: item.product.freight_tiers,
        variant_min_order_quantity: variantMoq,
      },
    };
  });
}

export function persistedLinesToCart(lines: PersistedCartLine[]): CartItem[] {
  return lines
    .filter((l) => l.productId > 0 && l.quantity > 0)
    .map((l) => ({
      product: {
        ...l.productSnapshot,
        id: l.productId,
      } as CartItem['product'],
      quantity: l.quantity,
      effectivePrice: l.effectivePrice,
      productVariantId: l.productVariantId ?? null,
      variantLabel: l.variantLabel ?? null,
      specRemark: l.specRemark ?? null,
      vatInvoiceType: l.vatInvoiceType ?? null,
      addedAt: l.addedAt,
    }));
}

export function loadPersistedCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedCartLine[];
    if (!Array.isArray(parsed)) return [];
    return persistedLinesToCart(parsed);
  } catch {
    return [];
  }
}

export function clearPersistedCart(): void {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    localStorage.removeItem(CART_IMPORT_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getOrCreateCartImportToken(): string {
  try {
    const existing = String(localStorage.getItem(CART_IMPORT_TOKEN_KEY) || '').trim();
    if (existing) return existing;
    const token =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `cart-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(CART_IMPORT_TOKEN_KEY, token);
    return token;
  } catch {
    return `cart-${Date.now()}`;
  }
}

export function persistedLinesToImportPayload(lines: PersistedCartLine[]) {
  return lines
    .filter((line) => line.productId > 0 && line.quantity > 0)
    .map((line) => ({
      product_id: line.productId,
      quantity: line.quantity,
      product_variant_id: line.productVariantId ?? null,
      vat_invoice_type: line.vatInvoiceType ?? undefined,
      spec_remark: line.specRemark ?? null,
    }));
}

export function savePersistedCart(cart: CartItem[]): void {
  try {
    if (!cart.length) {
      localStorage.removeItem(CART_STORAGE_KEY);
      return;
    }
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartToPersistedLines(cart)));
  } catch {
    /* quota / private mode */
  }
}
